
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/030e8b80f4f6c84b8c933549d525cf78.png"
              alt="Logyra AI"
              className="h-10"
            />
          </Link>
          <div className="flex items-center gap-8">
            <a
              href="#services"
              className={`text-sm font-medium transition-colors cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-black' : 'text-white/90 hover:text-white'
              }`}
            >
              Services
            </a>
            <a
              href="#process"
              className={`text-sm font-medium transition-colors cursor-pointer ${
                scrolled ? 'text-gray-700 hover:text-black' : 'text-white/90 hover:text-white'
              }`}
            >
              Process
            </a>
            <a
              href="#cta"
              className={`px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                scrolled
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              Book a Call
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b951bb2641c627777b9196528e743d0b.jpg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        </div>

        {/* Animated Floating Elements */}
        <div className="absolute top-32 left-20 w-3 h-3 bg-[#D4AF37]/40 rounded-full animate-pulse" />
        <div className="absolute top-48 right-32 w-2 h-2 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-40 left-40 w-4 h-4 bg-[#D4AF37]/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-60 right-20 w-2 h-2 bg-white/20 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8 border border-white/20">
            <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
            <span className="text-sm text-white/80 font-medium">Enterprise AI Solutions</span>
          </div>
          <h1 className="text-7xl font-bold text-white mb-12 tracking-tight leading-tight">
            Operational Intelligence.
            <br />
            Guaranteed Results.
          </h1>
          <p className="text-xl text-gray-300 mb-16 max-w-3xl mx-auto leading-relaxed">
            Logyra AI designs and deploys outcome-guaranteed AI systems that streamline operations,
            eliminate inefficiencies, and improve performance for service-based organizations.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="#cta"
              className="px-12 py-4 bg-white text-black text-sm font-semibold uppercase rounded-lg hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              Book a Strategy Call
            </a>
            <a
              href="#process"
              className="px-12 py-4 bg-transparent text-white text-sm font-semibold uppercase rounded-lg border-2 border-white hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              See How It Works
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <span className="text-xs text-white/50 uppercase tracking-widest">Scroll</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Trust / Positioning Statement */}
      <section className="py-32 bg-white relative overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20interconnected%20nodes%20and%20lines%20forming%20a%20network%20visualization%20in%20soft%20gray%20tones%20minimalist%20tech%20background%20subtle%20and%20elegant&width=800&height=600&seq=pattern001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="flex justify-center mb-6">
            <div className="w-32 h-px bg-[#D4AF37]" />
          </div>
          <p className="text-5xl font-light text-gray-800 mb-10 font-serif">
            Most AI solutions promise potential.
          </p>
          <h2 className="text-6xl font-bold text-black mb-12">
            Logyra AI guarantees performance.
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            We don't sell tools, experiments, or disconnected automations. We architect operational
            systems that run your business more efficiently — and we tie our work to measurable
            results.
          </p>
        </div>
      </section>

      {/* How Logyra AI Works */}
      <section id="process" className="py-32 bg-gray-50 relative overflow-hidden">
        {/* Background Visual */}
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20abstract%20gradient%20background%20with%20flowing%20curves%20and%20gentle%20waves%20in%20light%20gray%20and%20white%20tones%20creating%20a%20calm%20professional%20atmosphere%20minimalist%20corporate%20design&width=1920&height=800&seq=process001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-black mb-6">How Logyra AI Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A structured, outcome-focused approach to operational transformation
            </p>
          </div>

          <div className="grid grid-cols-4 gap-8 relative">
            {/* Connection Lines */}
            <div className="absolute top-16 left-0 right-0 h-px border-t-2 border-dotted border-gray-300 hidden lg:block" />

            {/* Step 1 */}
            <div className="bg-white rounded-xl p-12 relative shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center mb-6 relative z-10">
                <i className="ri-search-line text-3xl text-white"></i>
              </div>
              <div className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4">
                01
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Operational Analysis</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We identify inefficiencies, execution gaps, and performance bottlenecks across your
                workflows.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl p-12 relative shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center mb-6 relative z-10">
                <i className="ri-layout-grid-line text-3xl text-white"></i>
              </div>
              <div className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4">
                02
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">System Architecture</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We design AI-driven systems tailored to your operations — not generic templates.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl p-12 relative shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center mb-6 relative z-10">
                <i className="ri-rocket-line text-3xl text-white"></i>
              </div>
              <div className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4">
                03
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Deployment & Automation</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Systems are implemented, tested, and integrated into your real-world processes.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-xl p-12 relative shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center mb-6 relative z-10">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <div className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-4">
                04
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Measurement & Optimization</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Performance is tracked, refined, and optimized against defined outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Service Verticals */}
      <section id="services" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-black mb-6">Core Service Verticals</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized AI systems designed for operational excellence
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {/* Professional Services */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20environment%20with%20business%20people%20collaborating%20around%20digital%20screens%20showing%20data%20analytics%20clean%20minimalist%20corporate%20interior%20with%20warm%20lighting%20and%20sophisticated%20atmosphere&width=600&height=400&seq=service001&orientation=landscape"
                  alt="Professional Services"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-10">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-5">
                  PROFESSIONAL SERVICES
                </div>
                <div className="w-10 h-0.5 bg-[#D4AF37] mb-6" />
                <h3 className="text-2xl font-bold text-black mb-5">Professional Services</h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  AI systems that automate intake, routing, compliance workflows, and client
                  follow-ups — increasing utilization and reducing administrative drag.
                </p>
              </div>
            </div>

            {/* Aesthetics & Wellness */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20modern%20wellness%20clinic%20interior%20with%20clean%20white%20aesthetic%20medical%20equipment%20and%20calming%20atmosphere%20professional%20healthcare%20environment%20with%20soft%20natural%20lighting%20and%20minimalist%20design&width=600&height=400&seq=service002&orientation=landscape"
                  alt="Healthcare & Wellness"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-10">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-5">
                  HEALTHCARE & WELLNESS
                </div>
                <div className="w-10 h-0.5 bg-[#D4AF37] mb-6" />
                <h3 className="text-2xl font-bold text-black mb-5">
                  Aesthetics, Wellness & Med-Based Services
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  Operational intelligence that improves booking efficiency, reduces no-shows,
                  enhances patient journeys, and maximizes provider capacity.
                </p>
              </div>
            </div>

            {/* B2B Commercial */}
            <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Industrial%20operations%20control%20room%20with%20multiple%20monitors%20displaying%20workflow%20dashboards%20and%20analytics%20modern%20B2B%20commercial%20environment%20with%20professional%20team%20coordinating%20logistics%20clean%20tech%20aesthetic&width=600&height=400&seq=service003&orientation=landscape"
                  alt="B2B Operations"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-10">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-5">
                  B2B OPERATIONS
                </div>
                <div className="w-10 h-0.5 bg-[#D4AF37] mb-6" />
                <h3 className="text-2xl font-bold text-black mb-5">
                  B2B Commercial & Operational Services
                </h3>
                <p className="text-base text-gray-600 leading-relaxed">
                  AI-powered operations that streamline job coordination, reporting, and execution —
                  delivering predictable performance and margin control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 bg-[#0A1628] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20visualization%20with%20glowing%20connection%20points%20and%20flowing%20data%20streams%20in%20dark%20navy%20blue%20background%20futuristic%20technology%20pattern%20elegant%20and%20sophisticated&width=1920&height=600&seq=stats001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-6xl font-bold text-white mb-3">40%</div>
              <div className="text-sm text-gray-400 uppercase tracking-wider">Avg. Efficiency Gain</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-white mb-3">100+</div>
              <div className="text-sm text-gray-400 uppercase tracking-wider">Systems Deployed</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-white mb-3">98%</div>
              <div className="text-sm text-gray-400 uppercase tracking-wider">Client Retention</div>
            </div>
            <div className="text-center">
              <div className="text-6xl font-bold text-white mb-3">3x</div>
              <div className="text-sm text-gray-400 uppercase tracking-wider">ROI Average</div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-black mb-6">Proven Results Across Industries</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real operational improvements delivered through outcome-guaranteed AI systems
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8 mb-8">
            {/* Case Card 1 - Professional Services */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20law%20firm%20or%20consulting%20office%20with%20professionals%20working%20on%20laptops%20elegant%20corporate%20interior%20with%20glass%20walls%20and%20contemporary%20furniture%20warm%20professional%20lighting&width=600&height=300&seq=case001&orientation=landscape"
                  alt="Professional Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-4">
                  PROFESSIONAL SERVICES
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Professional Services Firm
                </h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Automated client intake, routing, and follow-ups using AI-driven workflows.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      42% reduction in administrative workload
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      31% faster client response times
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Improved utilization across advisory teams
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Outcome
                  </p>
                  <p className="text-sm text-black font-medium">
                    Streamlined operations with predictable performance gains.
                  </p>
                </div>
              </div>
            </div>

            {/* Case Card 2 - Med-Based Wellness */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20medical%20spa%20reception%20area%20with%20modern%20minimalist%20design%20white%20marble%20surfaces%20and%20calming%20ambient%20lighting%20professional%20wellness%20clinic%20aesthetic&width=600&height=300&seq=case002&orientation=landscape"
                  alt="Healthcare & Wellness"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-4">
                  HEALTHCARE & WELLNESS
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Med-Based Wellness Practice
                </h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Deployed AI systems to optimize booking, reduce no-shows, and automate patient engagement.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      28% increase in appointment utilization
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      35% reduction in no-shows
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Consistent patient follow-ups without added staff
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Outcome
                  </p>
                  <p className="text-sm text-black font-medium">
                    Higher revenue per provider with smoother daily operations.
                  </p>
                </div>
              </div>
            </div>

            {/* Case Card 3 - B2B Commercial */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300">
              <div className="h-40 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20logistics%20and%20operations%20center%20with%20team%20members%20coordinating%20on%20large%20digital%20displays%20showing%20workflow%20management%20systems%20industrial%20tech%20environment&width=600&height=300&seq=case003&orientation=landscape"
                  alt="B2B Operations"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-4">
                  B2B OPERATIONS
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  B2B Commercial Services Company
                </h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Implemented AI-assisted job coordination, reporting, and workflow automation.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      39% reduction in manual coordination tasks
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Faster job completion cycles
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Real-time operational visibility across teams
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Outcome
                  </p>
                  <p className="text-sm text-black font-medium">
                    More predictable delivery and improved operational control.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-8">
            {/* Case Card 4 - Multi-Location */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 flex">
              <div className="w-1/3 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Multiple%20office%20locations%20connected%20by%20digital%20network%20visualization%20modern%20corporate%20headquarters%20with%20satellite%20offices%20shown%20on%20interactive%20map%20display%20professional%20enterprise%20environment&width=400&height=500&seq=case004&orientation=portrait"
                  alt="Multi-Location"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 p-8">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-4">
                  CROSS-VERTICAL
                </div>
                <h3 className="text-xl font-bold text-black mb-4">
                  Multi-Location Service Organization
                </h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                  Unified disconnected workflows into a single AI-powered operational system.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Reduced process fragmentation
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Improved execution consistency across locations
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700 font-medium">
                      Clear performance metrics tied to outcomes
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    Outcome
                  </p>
                  <p className="text-sm text-black font-medium">
                    A scalable operational foundation built for growth.
                  </p>
                </div>
              </div>
            </div>

            {/* Case Card 5 - Outcome-Guaranteed */}
            <div className="bg-gradient-to-br from-[#0A1628] to-[#1a1a1a] rounded-2xl p-10 border border-[#D4AF37]/20 hover:shadow-xl transition-all duration-300 relative overflow-hidden">
              {/* Decorative Element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="text-xs font-semibold text-[#D4AF37] tracking-widest uppercase mb-4">
                  GUARANTEED RESULTS
                </div>
                <h3 className="text-2xl font-bold text-white mb-6">
                  Outcome-Guaranteed Deployments
                </h3>
                <p className="text-base text-gray-300 mb-8 leading-relaxed">
                  Every Logyra AI system is tied to defined performance metrics.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-base text-gray-200 font-medium">
                      Clear KPIs from day one
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-base text-gray-200 font-medium">
                      Ongoing optimization
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                    <p className="text-base text-gray-200 font-medium">
                      Systems that stay only if they perform
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#D4AF37]/20">
                  <p className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-2">
                    Outcome
                  </p>
                  <p className="text-base text-white font-medium">
                    Operational intelligence you can measure — and trust.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Logyra AI - Credibility */}
      <section className="py-28 bg-[#FAF9F6] relative overflow-hidden">
        {/* Background Visual */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/3 h-full opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20checkmark%20pattern%20with%20geometric%20shapes%20forming%20a%20verification%20symbol%20in%20gold%20and%20cream%20tones%20minimalist%20elegant%20design%20subtle%20texture&width=600&height=800&seq=why001&orientation=portrait"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-black mb-6">Why Logyra AI</h2>
            <p className="text-lg text-gray-600">
              Enterprise-grade intelligence with accountability built in
            </p>
          </div>

          <div className="grid grid-cols-2 gap-x-16 gap-y-10">
            <div className="flex items-center gap-5">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
              <p className="text-xl font-medium text-black" style={{ fontStretch: '95%' }}>
                Built for real operations — not demos
              </p>
            </div>

            <div className="flex items-center gap-5">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
              <p className="text-xl font-medium text-black" style={{ fontStretch: '95%' }}>
                Systems over tools
              </p>
            </div>

            <div className="flex items-center gap-5">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
              <p className="text-xl font-medium text-black" style={{ fontStretch: '95%' }}>
                Measurable performance, not promises
              </p>
            </div>

            <div className="flex items-center gap-5">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
              <p className="text-xl font-medium text-black" style={{ fontStretch: '95%' }}>
                Enterprise-grade architecture
              </p>
            </div>

            <div className="flex items-center gap-5 col-span-2 justify-center">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-lg"></i>
              </div>
              <p className="text-xl font-medium text-black" style={{ fontStretch: '95%' }}>
                Accountability baked into every deployment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Statement */}
      <section className="py-40 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20technology%20background%20with%20subtle%20glowing%20circuit%20patterns%20and%20flowing%20energy%20lines%20in%20deep%20navy%20blue%20and%20black%20tones%20sophisticated%20enterprise%20atmosphere%20with%20gold%20accent%20highlights&width=1920&height=800&seq=guarantee001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1628]/90" />
        </div>

        {/* Decorative Quotation Mark */}
        <div className="absolute top-12 right-12 text-white/8 text-9xl font-serif leading-none">
          "
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <div className="flex justify-center mb-8">
            <div className="w-52 h-0.5 bg-[#D4AF37]" />
          </div>

          <h2 className="text-5xl font-bold text-white mb-10 leading-snug">
            If our systems don't improve operational performance, they don't stay deployed.
          </h2>

          <div className="flex justify-center mb-8">
            <div className="w-52 h-0.5 bg-[#D4AF37]" />
          </div>

          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Every engagement is tied to clear metrics, defined outcomes, and continuous
            optimization — because intelligence only matters when it works.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section id="cta" className="py-36 bg-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 border border-[#D4AF37]/20 rounded-full" />
        <div className="absolute bottom-10 right-10 w-32 h-32 border border-[#D4AF37]/10 rounded-full" />
        <div className="absolute top-1/2 left-20 w-2 h-2 bg-[#D4AF37]/30 rounded-full" />
        <div className="absolute top-1/3 right-32 w-3 h-3 bg-[#D4AF37]/20 rounded-full" />

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-5xl font-semibold text-black mb-8">
            Ready to remove inefficiency from your operations?
          </h2>
          <p className="text-2xl text-gray-600 mb-16 leading-relaxed">
            Let's design a system that delivers results you can measure — and rely on.
          </p>
          <a
            href="https://calendly.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-16 py-5 bg-black text-white text-base font-semibold uppercase rounded-full hover:bg-gray-800 hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Schedule a Strategy Call
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#FAF9F6] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <img
                src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/030e8b80f4f6c84b8c933549d525cf78.png"
                alt="Logyra AI"
                className="h-10 mb-6"
              />
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Operational Intelligence.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">Guaranteed Results.</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Enterprise AI systems that work.
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">
                <a href="#services">Services</a>
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="#services"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    Professional Services
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    Healthcare & Wellness
                  </a>
                </li>
                <li>
                  <a
                    href="#services"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    B2B Operations
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">
                Company
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="#process"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    How It Works
                  </a>
                </li>
                <li>
                  <a
                    href="#cta"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    Book a Call
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    Case Studies
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-6">
                Contact
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="mailto:hello@logyra.ai"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    hello@logyra.ai
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-base text-gray-700 hover:text-black transition-colors cursor-pointer"
                  >
                    Twitter
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-12 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                © 2025 Logyra AI. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
